import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";

interface CookieConsentState {
  essential: boolean;
  marketing: boolean;
}

// Export function to show banner from anywhere
export const showCookieBanner = () => {
  window.dispatchEvent(new CustomEvent("show-cookie-banner"));
};

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem("jc-cookie-consent");
    if (!consent) {
      setShowBanner(true);
    }

    // Listen for manual trigger to show banner
    const handleShowBanner = () => setShowBanner(true);
    window.addEventListener("show-cookie-banner", handleShowBanner);
    return () => window.removeEventListener("show-cookie-banner", handleShowBanner);
  }, []);

  const acceptAll = () => {
    const consent: CookieConsentState = { essential: true, marketing: true };
    localStorage.setItem("jc-cookie-consent", JSON.stringify(consent));
    setShowBanner(false);
  };

  const acceptEssential = () => {
    const consent: CookieConsentState = { essential: true, marketing: false };
    localStorage.setItem("jc-cookie-consent", JSON.stringify(consent));
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-6 md:max-w-md">
      <div className="relative bg-black/90 backdrop-blur-xl border border-accent-purple/30 rounded-2xl p-5 shadow-2xl shadow-accent-purple/10 overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Purple orb - bottom left */}
          <div 
            className="absolute -bottom-4 -left-4 w-24 h-24 rounded-full bg-accent-purple/30 blur-xl"
            style={{
              animation: 'float 6s ease-in-out infinite',
            }}
          />
          {/* Orange orb - bottom center */}
          <div 
            className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-32 h-20 rounded-full bg-orange-500/25 blur-xl"
            style={{
              animation: 'float 8s ease-in-out infinite reverse',
            }}
          />
          {/* Small purple orb - center right */}
          <div 
            className="absolute top-1/2 -right-2 w-16 h-16 rounded-full bg-accent-purple/20 blur-lg"
            style={{
              animation: 'float 5s ease-in-out infinite 1s',
            }}
          />
          {/* Tiny accent dots */}
          <div 
            className="absolute bottom-8 left-1/4 w-2 h-2 rounded-full bg-orange-400/60"
            style={{
              animation: 'pulse-slow 3s ease-in-out infinite',
            }}
          />
          <div 
            className="absolute bottom-12 right-1/3 w-1.5 h-1.5 rounded-full bg-accent-purple/80"
            style={{
              animation: 'pulse-slow 4s ease-in-out infinite 0.5s',
            }}
          />
          <div 
            className="absolute bottom-6 right-1/4 w-1 h-1 rounded-full bg-orange-300/70"
            style={{
              animation: 'pulse-slow 2.5s ease-in-out infinite 1s',
            }}
          />
        </div>
        
        {/* Content */}
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-2 h-2 rounded-full bg-gradient-to-r from-accent-purple to-orange-500 animate-pulse" />
            <h3 className="font-semibold text-foreground text-sm">Cookie-Einstellungen</h3>
          </div>
          <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
            Wir verwenden technisch notwendige Cookies für grundlegende Funktionen sowie das Meta Pixel zur Optimierung unserer Werbemaßnahmen. 
            Das Meta Pixel ist als essentieller Dienst für den Betrieb unserer Website erforderlich.
          </p>
          <p className="text-xs text-muted-foreground/70 mb-4">
            Mehr Infos in unserer <a href="/datenschutz" className="underline hover:text-accent-purple transition-colors">Datenschutzerklärung</a>.
          </p>
          <div className="flex gap-3 justify-end">
            <Button
              size="sm"
              variant="outline"
              onClick={acceptEssential}
              className="text-xs font-medium border-white/20 hover:border-accent-purple/50 hover:bg-accent-purple/10 transition-all"
            >
              Nur notwendige
            </Button>
            <Button
              size="sm"
              onClick={acceptAll}
              className="text-xs font-medium bg-gradient-to-r from-accent-purple to-orange-500 text-white hover:opacity-90 transition-opacity border-0"
            >
              Alle akzeptieren
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
